import { Stack, useTheme } from "@mui/material";
import { FoodsIconButton, FoodsTags, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { SsoProtocol } from "src/pages/Authentication/ssoConnector";
import { getProviderTagLabel, getProviderTagVariant, ProviderProtocol } from "../ssoProviderModel";

type SsoProviderRowProps = {
    buttonLabel: string | undefined;
    isFirst?: boolean;
    mode: ProviderProtocol | null;
    onDelete: () => void;
    onEdit: () => void;
    protocol: SsoProtocol;
    providerName: string;
};

export const SsoProviderRow = ({
    buttonLabel,
    isFirst = false,
    mode,
    onDelete,
    onEdit,
    protocol,
    providerName,
}: SsoProviderRowProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const tagLabel = getProviderTagLabel(protocol, mode);

    return (
        <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            gap={theme.spacing(1)}
            sx={{
                padding: `${theme.spacing(1.5)} ${theme.spacing(1)} ${theme.spacing(1.5)} ${theme.spacing(2)}`,
                borderTop: isFirst ? "none" : `1px solid ${theme.custom.grey[500]}`,
            }}
        >
            <Stack gap={theme.spacing(0.5)} sx={{ minWidth: 0 }}>
                <Stack flexDirection="row" alignItems="center" gap={theme.spacing(1)} sx={{ minWidth: 0 }}>
                    <Typography variant="body-bold">{providerName}</Typography>
                    <FoodsTags label={tagLabel} variant={getProviderTagVariant(mode)} />
                </Stack>
                {buttonLabel !== undefined && buttonLabel !== providerName && (
                    <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                        {buttonLabel}
                    </Typography>
                )}
            </Stack>
            <Stack flexDirection="row" gap={theme.spacing(0.5)} sx={{ flexShrink: 0 }}>
                <FoodsIconButton
                    size="s"
                    icon="edit"
                    tooltip={{ text: t("common.actions.edit", "Edit") }}
                    buttonProps={{
                        "aria-label": t("settings.sso.providers.edit_named", "Edit {{provider}}", {
                            provider: providerName,
                        }),
                        onClick: onEdit,
                    }}
                />
                <FoodsIconButton
                    size="s"
                    icon="delete"
                    tooltip={{ text: t("common.actions.delete", "Delete") }}
                    buttonProps={{
                        "aria-label": t("settings.sso.providers.delete_named", "Delete {{provider}}", {
                            provider: providerName,
                        }),
                        onClick: onDelete,
                        sx: { color: theme.palette.error.main },
                    }}
                />
            </Stack>
        </Stack>
    );
};
